import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Paper, Typography, IconButton } from '@material-ui/core';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import moment from 'moment';
import * as strings from '../../helpers/strings';
import '../../index.css';

export class PortfolioFaultLog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 0,
      rowsPerPage: 25,
      sortField: 'timestamp',
      sortOrder: 'desc',
    };
  }

  // Remove ONLY a trailing "Not Error" or "Error" (case-insensitive)
  formatDeviceStatus = (status) => {
    // Guard: if status is empty or not a string, return empty string
    if (!status || typeof status !== 'string') return '';
    let s = status;

    // 1) Cosmetic: normalize spaces around colons. Turns "Shutdown :Fault" into "Shutdown: Fault"
    s = s.replace(/\s*:\s*/g, ': ');

    // 2) Remove a trailing "Not Error" OR "Error" at the END of the string (case-insensitive).
    //    - No 'g' flag, so it runs once.
    //    - '$' anchor means: only if it appears at the very end.
    //    - '\b' word boundaries make sure we match whole words.
    //    - '\s*$' lets trailing spaces after Error/Not Error be removed too.
    s = s.replace(/\b(?:Not\s+)?Error\b\s*$/i, '');

    // 3) If removing that leaves a dangling ":" or "-" at the very end, remove that punctuation.
    s = s.replace(/[:\-–—]\s*$/,'');

    // 4) Clean up any double spaces and trim ends.
    s = s.replace(/\s{2,}/g, ' ').trim();

    return s;
  };

  formatDate = (timestamp) => {
    const timestampInMilliseconds =
      timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
    return moment(timestampInMilliseconds).format('YYYY-MM-DD hh:mm:ss A');
  };

  // Convert NaN, null, undefined, empty string, or "NaN" string to "-"
  formatValue = (value) => {
    if (value === null || value === undefined || value === '' || 
        value === 'NaN' || (typeof value === 'number' && isNaN(value))) {
      return '-';
    }
    return value;
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  handleSort = (field) => {
    const { sortField, sortOrder } = this.state;
    const newSortOrder =
      field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    this.setState({ sortField: field, sortOrder: newSortOrder });
  };

  getSortedData = (data) => {
    const { sortField, sortOrder } = this.state;
    if (!sortField) return data;
  
    return [...data].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
  
      // Natural numeric sort for deviceName (like inverter1 to inverter12)
      if (sortField === 'deviceName') {
        const extractNumber = (val) => {
          const match = val?.match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        };
  
        const aNum = extractNumber(aValue);
        const bNum = extractNumber(bValue);
  
        if (aNum !== bNum) {
          return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
        }
  
        // Fallback string comparison if numbers are equal
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
  
      // Special case: timestamp sort
      if (sortField === 'timestamp') {
        aValue = new Date(a.timestamp).getTime();
        bValue = new Date(b.timestamp).getTime();
      }
  
      // Generic string/number comparison
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  getPageRange = (totalPages, currentPage) => {
    const delta = 1;
    const range = [];
    const left = Math.max(0, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    if (left > 0) {
      range.push(0);
      if (left > 1) range.push('...');
    }

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < totalPages - 1) {
      if (right < totalPages - 2) range.push('...');
      range.push(totalPages - 1);
    }

    return range;
  };

  getFaultTypeStyle = (faultType) => {
    if (!faultType) return {};
    const type = faultType.toLowerCase();
    if (type.includes('critical')) return { color: '#B71C1C'};
    if (type.includes('warning')) return { color: '#FB8C00'};
    return {};
  };
  
  render() {
    const { portfolioFaultData, selectedPortfolioFaultLevel } = this.props;
    const { page, rowsPerPage } = this.state;

  // Flatten first
  const flattenedRows = [];
  portfolioFaultData.forEach((fault) => {
    fault.deviceMaps.forEach((device) => {
      flattenedRows.push({
        ...device,
        timestamp: fault.timestamp,
        stationId: fault.stationId,
      });
    });
  });

  // Sort the flattened rows
  const sortedData = this.getSortedData(flattenedRows);

  // Paginate sorted data
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedRows = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

    let counter = page * rowsPerPage + 1;
    const portfolioLevelText = `${selectedPortfolioFaultLevel.charAt(0).toUpperCase() + selectedPortfolioFaultLevel.slice(1)
      }`;

    return (
      <Paper className="portfolioFaultLog">
        {/* <Typography className="portfolioFaultTitle" variant="h6" color="primary">
          {strings.lblSmartOMDetection}
        </Typography> */}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: 10,
            gap: '10px',
          }}
        >
          <Typography className='portfolioFaultLevelLog' variant="subtitle1">
            <strong>📍 Portfolio Selection: {portfolioLevelText}</strong>
          </Typography>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography>Rows per page:</Typography>
              <select
                value={rowsPerPage}
                onChange={this.handleChangeRowsPerPage}
                style={{ padding: '6px 8px', borderRadius: '4px' }}
              >
                {[5, 10, 25, 50].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Smart Pagination */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {/* Prev */}
              <button
                disabled={page === 0}
                onClick={() => this.handleChangePage(null, page - 1)}
                style={{
                  border: '1px solid #343A40',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  color: '#343A40',
                  cursor: page === 0 ? 'not-allowed' : 'pointer',
                  opacity: page === 0 ? 0.5 : 1,
                }}
              >
                ‹
              </button>

              {/* Page numbers */}
              {this.getPageRange(totalPages, page).map((item, index) =>
                item === '...' ? (
                  <span key={index} style={{ padding: '6px 12px' }}>...</span>
                ) : (
                  <button
                  key={`page-${item}-${index}`}
                  onClick={() => this.handleChangePage(null, item)}
                    style={{
                      fontSize: '0.75rem',
                      border: '1px solid #343A40',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      backgroundColor: page === item ? '#343A40' : '#fff',
                      color: page === item ? '#fff' : '#343A40',
                      fontWeight: page === item ? 'bold' : 'normal',
                      cursor: 'pointer',
                      minWidth: '40px',
                    }}
                  >
                    {item + 1}
                  </button>
                )
              )}

              {/* Next */}
              <button
                disabled={page === totalPages - 1}
                onClick={() => this.handleChangePage(null, page + 1)}
                style={{
                  border: '1px solid #343A40',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  color: '#343A40',
                  cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer',
                  opacity: page === totalPages - 1 ? 0.5 : 1,
                }}
              >
                ›
              </button>
            </div>
          </div>
        </div>

        {flattenedRows.length > 0 ? (
          <div className="portfolioFaultDetection">
            <ul className="FaultList">
              <li className="FaultListHeader">
                <span>No</span>
                <span>
                  Station
                  <IconButton
                    onClick={() => this.handleSort('stationId')}
                    size="small"
                    className="sortIconStyle"
                  >
                    <UnfoldMoreIcon fontSize="inherit" />
                  </IconButton>
                </span>
                <span>
                  Date
                  <IconButton
                    onClick={() => this.handleSort('timestamp')}
                    size="small"
                    className="sortIconStyle"
                  >
                    <UnfoldMoreIcon fontSize="inherit" />
                  </IconButton>
                </span>
                <span>
                  Inverter
                  <IconButton
                    onClick={() => this.handleSort('deviceName')}
                    size="small"
                    className="sortIconStyle"
                  >
                    <UnfoldMoreIcon fontSize="inherit" />
                  </IconButton>
                </span>
                <span>Brand</span>
                <span>Model</span>
                <span>Status</span>
                <span>Alarm Code</span>
                <span>
                  Alarm Type
                  <IconButton
                    onClick={() => this.handleSort('faultType')}
                    size="small"
                    className="sortIconStyle"
                  >
                    <UnfoldMoreIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </li>
              {paginatedRows.map((row, index) => (
                <li className="FaultListBody" key={index}>
                  <span className="faultItemNo">{counter++}</span>
                  <span className="faultItemStation">{row.stationId}</span>
                  <span className="faultItemDate">{this.formatDate(row.timestamp)}</span>
                  <span className="faultItemDeviceName">{this.formatValue(row.deviceName)}</span>
                  <span>{this.formatValue(row.deviceBrand)}</span>
                  <span>{this.formatValue(row.deviceModel)}</span>
                  <span className="faultItemStatus">
                    {this.formatValue(this.formatDeviceStatus(row.deviceStatus))}
                  </span>
                  <span className="faultItemError">
                    {Array.isArray(row.errorCode) && row.errorCode.length > 0
                      ? this.formatValue(row.errorCode.filter((code) => code && code !== 'NaN' && code !== 'null').join(', '))
                      : '-'}
                  </span>
                  <span className="faultItemType" style={this.getFaultTypeStyle(row.faultType)}>
                    {this.formatValue(row.faultType)}
                  </span>                
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Typography variant="subtitle1" style={{ margin: '20px' }}>
            No data available. Please submit to see the fault details.
          </Typography>
        )}
      </Paper>
    );
  }
}

const mapStateToProps = (state) => ({
  portfolioFaultData: state.portfolioFaultOverview?.portfolioFaultLogData || [],
  selectedPortfolioFaultLevel: state.portfolioFaultOverview?.selectedPortfolioFaultLevel || 'all',
});

export default connect(mapStateToProps)(PortfolioFaultLog);
